import DoneAllRounded from '@mui/icons-material/DoneAllRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Card,
  Skeleton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, unwrap, type Schemas } from '../../../api/client';
import { EmptyState } from '../../../components/states';
import { dateTime, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { IconBadge, QueryError, Segmented, type Tone } from '../components/ui';
import { linkTo } from '../components/useEntityLink';

type Notification = Schemas['Notification'];

const SEVERITY: Record<Notification['severity'], { tone: Tone; icon: ReactNode }> = {
  INFO: { tone: 'info', icon: <InfoOutlined /> },
  WARNING: { tone: 'warning', icon: <WarningAmberRounded /> },
  CRITICAL: { tone: 'error', icon: <ErrorOutlineRounded /> },
};

/** The signed-in user's notifications, with unread filter and mark-as-read. */
export function NotificationList() {
  const qc = useQueryClient();
  const notify = useNotify();
  const navigate = useNavigate();
  const { format } = useShop();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const query = { page, pageSize, unread: filter === 'unread' ? ('true' as const) : undefined };
  const q = useQuery({
    queryKey: ['notifications', 'list', query],
    queryFn: () => unwrap(api.GET('/notifications', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['notifications'] });
  const markOne = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/notifications/{id}/read', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const markAll = useMutation({
    mutationFn: () => unwrap(api.POST('/notifications/read-all')),
    onSuccess: (r) => {
      refresh();
      notify.success(r.updated ? `Marked ${String(r.updated)} as read` : 'All caught up');
    },
    onError: (e) => notify.error(e),
  });
  const unread = q.data?.unreadCount ?? 0;

  const open = (n: Notification) => {
    if (!n.readAt) markOne.mutate(n.id);
    const to = linkTo(n.entityType, n.entityId);
    if (to) void navigate(to);
  };

  return (
    <Card>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        <Segmented
          label="Show"
          value={filter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'unread', label: unread ? `Unread (${String(unread)})` : 'Unread' },
          ]}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<DoneAllRounded />}
          disabled={unread === 0}
          loading={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          Mark all as read
        </Button>
      </Stack>
      {q.error && (
        <Box sx={{ p: 2 }}>
          <QueryError error={q.error} onRetry={() => void q.refetch()} />
        </Box>
      )}
      {q.isLoading &&
        Array.from({ length: 5 }, (_, i) => (
          <Stack
            key={i}
            direction="row"
            spacing={2}
            sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Skeleton variant="rounded" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="40%" />
              <Skeleton width="80%" />
            </Box>
          </Stack>
        ))}
      {q.data?.data.length === 0 && (
        <EmptyState
          icon={<NotificationsNoneRounded />}
          title={filter === 'unread' ? 'You’re all caught up' : 'No notifications yet'}
          body="Reminders about due invoices, low stock and more will show up here."
        />
      )}
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {q.data?.data.map((n) => {
          const sev = SEVERITY[n.severity];
          const isUnread = !n.readAt;
          return (
            <Box
              component="li"
              key={n.id}
              onClick={() => open(n)}
              sx={{
                display: 'flex',
                gap: 2,
                px: 2.5,
                py: 1.75,
                borderBottom: 1,
                borderColor: 'divider',
                cursor: 'pointer',
                position: 'relative',
                bgcolor: isUnread
                  ? 'color-mix(in srgb, var(--mui-palette-primary-main) 4%, transparent)'
                  : undefined,
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <IconBadge icon={sev.icon} tone={sev.tone} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: isUnread ? 700 : 550, flex: 1, minWidth: 0 }}
                  >
                    {n.title}
                  </Typography>
                  <Tooltip title={dateTime(n.createdAt, format)}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {relativeTime(n.createdAt)}
                    </Typography>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {n.body}
                </Typography>
              </Box>
              {isUnread && (
                <Box
                  aria-label="Unread"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    mt: 0.75,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      {q.data && q.data.page.total > 25 && (
        <TablePagination
          component="div"
          count={q.data.page.total}
          page={page - 1}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, p) => setPage(p + 1)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      )}
    </Card>
  );
}
